import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Banner from "../components/Banner";
import { fetchEvents } from "../api/NewsEventsApi";
import { IBannerData, INewsEventsData } from "../exports/interfaces";
import { useLoader } from "../context/LoaderContext";
import { fetchEventBanner } from "../api/BannerApi";
import { dynamicFailed } from "../hooks/custom/useToast";
import { PdfViwer } from "../components/PdfViwer";

const EventsDetailPage = () => {
  const location = useLocation();
  const { setLoading } = useLoader();
  const params = useParams();
  const state = location.state as { post?: INewsEventsData } | null;
  const [post, setPost] = useState<INewsEventsData | undefined>(state?.post);
  const eventId = params.id ? Number(params.id) : undefined;
  const imageUrls =
    post?.image?.map((item) => item.media.url) ??
    post?.thumbnail?.map((item) => item.media.url) ??
    [];
  const [eventBanner, setEventBanner] = useState<IBannerData[]>([]);

  const [openPdf, setOpenPdf] = useState(false);
  const [pdfPath, setPdfPath] = useState("");

  const fileSource = post?.pdf_file?.length ? post.pdf_file : post?.file;
  const pdfUrl = fileSource?.[0]?.media?.url;

  const openViewer = (path: string) => {
    setPdfPath(path);
    setOpenPdf(true);
  };

  const closeViewer = () => setOpenPdf(false);

  useEffect(() => {
    setLoading(true);
    if (!post && eventId) {
      fetchEvents()
        .then((eventsData: INewsEventsData[] | undefined) => {
          const found = eventsData?.find(
            (item: INewsEventsData) => item.id === eventId,
          );
          if (found) {
            setPost(found);
          }
        })
        .catch((error) => {
          dynamicFailed(error.response?.data?.error?.message);
        })
        .finally(() => {
          setTimeout(() => {
            setLoading(false);
          }, 1000);
        });
      return;
    }
    Promise.all([fetchEventBanner()])
      .then(([eventBannerData]) => {
        setEventBanner(eventBannerData);
      })
      .catch((error) => {
        dynamicFailed(error.response.data.error.message);
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      });
  }, [setLoading, post, eventId]);

  return (
    <>
      <Banner
        title={post?.title ? "Events" : "Event Detail"}
        bannerImage={eventBanner?.[0]?.image?.[0]}
      />

      <section id="events-detail">
        <div className="td_height_112 td_height_lg_40" />
        <div className="container">
          <div
            className="td_section_heading td_style_1 text-center wow fadeInUp"
            data-wow-duration="1s"
            data-wow-delay="0.2s"
          >
            <h2 className="td_section_title td_fs_48 mb-0">Event Details</h2>
          </div>

          <div className="td_height_40 td_height_lg_30" />

          {post ? (
            <div className="td_post td_style_1">
              <div className="td_post_info">
                <div className="td_post_meta td_fs_14 td_medium td_mb_20">
                  <span>{post.date}</span>
                </div>
                <h2 className="td_post_title td_fs_36 td_medium td_mb_16">
                  {post.title}
                </h2>
                <p className="td_post_subtitle td_mb_24 td_heading_color td_opacity_7">
                  {post.sub_title}
                </p>

                {imageUrls.length > 0 && (
                  <div className="td_post_thumb td_mb_30">
                    <div className="row g-3">
                      {imageUrls.map((url, index) => (
                        <div
                          className="col-12"
                          key={`event-image-${index}`}
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <img
                            src={process.env.REACT_APP_cmsUrl + url}
                            alt={`${post.title}${index + 1}`}
                            style={{
                              width: "auto",
                              maxHeight: "750px",
                              borderRadius: "20px",
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {post.text ? (
                  <div
                    className="td_post_content td_heading_color"
                    style={{ lineHeight: 1.9 }}
                    dangerouslySetInnerHTML={{ __html: post.text }}
                  />
                ) : (
                  <p className="td_post_subtitle td_heading_color td_opacity_7">
                    No additional content is available for this event.
                  </p>
                )}

                <div className="td_height_50 td_height_lg_30" />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    gap: "10px",
                  }}
                >
                  {pdfUrl && (
                    <Link
                      to="#"
                      className="td_btn td_style_1 td_type_3 td_radius_30 td_medium"
                      onClick={(e) => {
                        e.preventDefault();
                        openViewer(fileSource?.[0]?.media?.url || "");
                      }}
                    >
                      <span className="td_btn_in td_accent_color">
                        <span>View PDF</span>
                      </span>
                    </Link>
                  )}

                  <Link
                    to="/events"
                    className="td_btn td_style_1 td_type_3 td_radius_30 td_medium"
                  >
                    <span className="td_btn_in td_accent_color">
                      <span>Back to Events</span>
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="td_fs_18 td_heading_color td_opacity_7">
                Event details are unavailable. Please return to the events list.
              </p>
              <div className="td_height_30 td_height_lg_30" />
              <Link
                to="/events"
                className="td_btn td_style_1 td_type_3 td_radius_30 td_medium"
              >
                <span className="td_btn_in td_accent_color">
                  <span>Return to Events</span>
                </span>
              </Link>
            </div>
          )}
        </div>

        <div className="td_height_120 td_height_lg_40" />
        <PdfViwer open={openPdf} onClose={closeViewer} path={pdfPath} />
      </section>
    </>
  );
};

export default EventsDetailPage;
